import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import {
  useHRExportLasDaysForUserHook,
  useHRExportLasDaysOverviewHook,
  useHRGetLasDaysForUser,
  useHRGetLasDaysOverview,
} from 'api/hr/hr';
import { LasDaysMonthDTO, LasDaysOverviewRowDTO } from 'api/model';
import Table from 'components/Table';
import { ColumnSetting } from 'components/Table/utils';
import Button from 'components/inputs/Button';
import Checkbox from 'components/inputs/Checkbox';
import Input from 'components/inputs/Input';
import SectionHeader from 'components/SectionHeader';
import TransparentSpinner from 'components/spinners/TransparentSpinner';
import { RequestStatus, useApiCallFn } from 'hooks/useApiCallFn';
import { downloadBlob } from 'utils/file-utils';
import { normalizeLasDaysRow, normalizeLasDaysUser } from './normalizeLasDays';

// HR > LAS-dagar. Räkneregeln och urvalet bor i backend (LasDaysCalculator / LasDaysLogic);
// sidan visar bara resultatet. Se .claude/features/las-days.md.

const MONTH_NAMES = [
  'januari',
  'februari',
  'mars',
  'april',
  'maj',
  'juni',
  'juli',
  'augusti',
  'september',
  'oktober',
  'november',
  'december',
];

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 10px 0;
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 15px;
`;

const Info = styled.p`
  margin: 0;
  max-width: 900px;
  color: ${({ theme }) => theme.colors.foreground.tertiary};
`;

const Summary = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 30px;

  strong {
    font-size: ${({ theme }) => theme.sizes.font.medium};
  }
`;

const Reached = styled.span`
  font-weight: 700;
  color: ${({ theme }) => theme.colors.foreground.warning};
`;

const ClickableTable = styled(Table)`
  .las-row {
    cursor: pointer;
  }
  .las-row-selected,
  .las-row-reached-month {
    font-weight: 700;
  }
` as typeof Table;

const ruleText =
  'Regel: har personen arbetat 1–2 dagar under en kalendermånad räknas bara de dagarna. ' +
  'Har personen arbetat minst 3 dagar räknas alla kalenderdagar från första till sista ' +
  'arbetade dagen den månaden (även helger och lediga dagar däremellan). ' +
  'Arbetad dag = ett påbörjat uppdrag (oavsett hur det slutade) eller ett avbokat ' +
  'uppdrag som ger standbytid, räknat på startdagen respektive den bokade dagen. ' +
  'Uppgifter finns från när Zäkra började användas.';

const LasDaysPage = () => {
  const [includeFormer, setIncludeFormer] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedUserId, setSelectedUserId] = useState<number>();

  const overview = useHRGetLasDaysOverview({
    includeFormerEmployees: includeFormer,
  });
  const detail = useHRGetLasDaysForUser(
    { userId: selectedUserId ?? 0 },
    { query: { enabled: selectedUserId !== undefined } }
  );

  const exportOverview = useHRExportLasDaysOverviewHook();
  // Flaggan skickas med i run() i stället för att fångas i closuren: useApiCallFn memoiserar
  // anroparen, så en fångad includeFormer blev kvar på värdet från första renderingen.
  const exportOverviewCall = useApiCallFn(
    (includeFormerEmployees: boolean) =>
      exportOverview({ includeFormerEmployees }),
    [exportOverview]
  );
  const exportForUser = useHRExportLasDaysForUserHook();
  const exportForUserCall = useApiCallFn(
    (userId: number) => exportForUser({ userId }),
    [exportForUser]
  );

  // Datumen kan vara Date vid runtime trots string-typen, se normalizeLasDays.
  const overviewRows = useMemo(
    () => (overview.data ?? []).map(normalizeLasDaysRow),
    [overview.data]
  );
  const user = useMemo(
    () => (detail.data ? normalizeLasDaysUser(detail.data) : undefined),
    [detail.data]
  );

  const rows = useMemo(() => {
    const all = overviewRows;
    const query = search.trim().toLowerCase();
    if (!query) return all;
    return all.filter(
      (r) =>
        r.name.toLowerCase().includes(query) ||
        (r.employmentID ?? '').toLowerCase().includes(query)
    );
  }, [overviewRows, search]);

  // Bredderna rymmer rubriken i fetstil (med sorteringspilen) och ett helt ÅÅÅÅ-MM-DD även på
  // fetmarkerade rader.
  const overviewColumns: ColumnSetting<LasDaysOverviewRowDTO, any>[] = [
    {
      head: 'Namn',
      render: (r) => r.name,
      width: 150,
      sortFunction: (a, b) => a.name.localeCompare(b.name),
    },
    {
      head: 'Anst.nr',
      render: (r) => r.employmentID ?? '',
      width: 75,
      sortFunction: (a, b) =>
        (a.employmentID ?? '').localeCompare(b.employmentID ?? '', 'sv', {
          numeric: true,
        }),
    },
    {
      head: 'Anställd',
      render: (r) => (r.isEmployed ? 'Ja' : 'Nej'),
      width: 70,
    },
    {
      head: 'Heltid',
      render: (r) => (r.isFullTime ? 'Ja' : 'Nej'),
      width: 55,
    },
    {
      head: 'Första dag',
      render: (r) => r.firstWorkedDate,
      width: 100,
      sortFunction: (a, b) =>
        a.firstWorkedDate.localeCompare(b.firstWorkedDate),
    },
    {
      head: 'Senaste dag',
      render: (r) => r.lastWorkedDate,
      width: 110,
      sortFunction: (a, b) => a.lastWorkedDate.localeCompare(b.lastWorkedDate),
    },
    {
      head: 'Arbetade dagar',
      render: (r) => r.workedDayCount,
      width: 130,
      sortFunction: (a, b) => a.workedDayCount - b.workedDayCount,
    },
    {
      head: 'LAS-dagar',
      render: (r) => r.lasDays,
      width: 100,
      sortFunction: (a, b) => a.lasDays - b.lasDays,
    },
    {
      head: `Nådde ${overview.data?.[0]?.thresholdDays ?? 361}`,
      render: (r) =>
        r.thresholdReachedDate ? (
          <Reached>{r.thresholdReachedDate}</Reached>
        ) : (
          `${r.thresholdDays - r.lasDays} kvar`
        ),
      width: 95,
      sortFunction: (a, b) =>
        (a.thresholdReachedDate ?? '9999').localeCompare(
          b.thresholdReachedDate ?? '9999'
        ),
    },
  ];

  const monthColumns: ColumnSetting<LasDaysMonthDTO, any>[] = [
    {
      head: 'Månad',
      render: (m) => `${MONTH_NAMES[m.month - 1]} ${m.year}`,
      width: 120,
    },
    { head: 'Arbetade dagar', render: (m) => m.workedDayCount, width: 115 },
    { head: 'Första', render: (m) => m.firstWorkedDate, width: 90 },
    { head: 'Sista', render: (m) => m.lastWorkedDate, width: 90 },
    {
      head: 'Räknas som',
      render: (m) => (m.isSpanCounted ? 'Första–sista' : 'Arbetade dagar'),
      width: 115,
    },
    { head: 'Räknade dagar', render: (m) => m.countedDays, width: 110 },
    { head: 'Ackumulerat', render: (m) => m.cumulativeDays, width: 95 },
    {
      head: 'Arbetade datum',
      render: (m) => m.workedDates.map((d) => Number(d.slice(8))).join(', '),
      width: 200,
    },
  ];

  const handleExportOverview = async () => {
    const [result] = await exportOverviewCall.run(includeFormer);
    if (result) downloadBlob(result, 'LAS-dagar.xlsx');
  };

  const handleExportUser = async () => {
    if (!user) return;
    const [result] = await exportForUserCall.run(user.userID);
    const name = [user.name, user.employmentID]
      .filter(Boolean)
      .join(' ');
    if (result) downloadBlob(result, `LAS-dagar ${name}.xlsx`);
  };

  return (
    <Wrapper>
      <Info>{ruleText}</Info>

      {selectedUserId !== undefined && (
        <Section>
          <SectionHeader>
            {user
              ? `${user.name}${
                  user.employmentID
                    ? ` (${user.employmentID})`
                    : ''
                }`
              : detail.isError
                ? 'Kunde inte hämta LAS-dagar'
                : 'Laddar…'}
          </SectionHeader>
          {detail.isLoading && <TransparentSpinner />}
          {detail.isError && (
            <Toolbar>
              <Info>Underlaget för personen kunde inte hämtas.</Info>
              <Button onClick={() => detail.refetch()}>Försök igen</Button>
              <Button onClick={() => setSelectedUserId(undefined)}>
                Stäng
              </Button>
            </Toolbar>
          )}
          {user && (
            <>
              <Summary>
                <span>
                  LAS-dagar: <strong>{user.lasDays}</strong>
                </span>
                <span>
                  Arbetade dagar: <strong>{user.workedDayCount}</strong>
                </span>
                <span>
                  {user.thresholdReachedDate ? (
                    <>
                      Nådde {user.thresholdDays} dagar:{' '}
                      <Reached>{user.thresholdReachedDate}</Reached>
                    </>
                  ) : (
                    `Har inte nått ${user.thresholdDays} dagar`
                  )}
                </span>
              </Summary>
              <Toolbar>
                <Button
                  onClick={handleExportUser}
                  disabled={exportForUserCall.status === RequestStatus.Fetching}
                >
                  <FontAwesomeIcon icon={faDownload} /> Ladda ner månadsunderlag
                </Button>
                <Button onClick={() => setSelectedUserId(undefined)}>
                  Stäng
                </Button>
              </Toolbar>
              <ClickableTable
                columnSettings={monthColumns}
                rows={user.months}
                rowClassName={(m) =>
                  `las-month-${m.year}-${m.month}${
                    m.thresholdReachedInMonth ? ' las-row-reached-month' : ''
                  }`
                }
              />
            </>
          )}
        </Section>
      )}

      <Section>
        <SectionHeader>Alla med arbetade dagar</SectionHeader>
        <Toolbar>
          <Input
            placeholder="Sök namn eller anställningsnummer"
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
          />
          <Checkbox
            checked={includeFormer}
            onChange={() => setIncludeFormer((v) => !v)}
          >
            Visa även tidigare anställda
          </Checkbox>
          <Button
            onClick={handleExportOverview}
            disabled={exportOverviewCall.status === RequestStatus.Fetching}
          >
            <FontAwesomeIcon icon={faDownload} /> Ladda ner översikt
          </Button>
        </Toolbar>
        {overview.isLoading ? (
          <TransparentSpinner />
        ) : (
          <ClickableTable
            columnSettings={overviewColumns}
            rows={rows}
            onRowClick={(r) => {
              setSelectedUserId(r.userID);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            rowClassName={(r) =>
              `las-row las-user-${r.userID}${
                r.userID === selectedUserId ? ' las-row-selected' : ''
              }`
            }
          />
        )}
      </Section>
    </Wrapper>
  );
};

export default LasDaysPage;
